/* eslint-disable max-len */
// Copyright (c) 2022 Celadon Development LLC, All rights reserved.
// Author Alexander Mikholap <alexander.miholap@celadon.ae>
import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import {
  Loader,
  ConfirmModal,
} from '../../../components';
import { VoucherInfo, Redeem } from '../components';
import {
  getLoading,
  getBookshopCustomer,
  getValue,
  getValueStatus,
  getConfirmModal,
} from '../store/selectors';
import * as voucherConfirmedActions from '../store/actions';

import history from '../../../services/history';
import { redeemAmountError } from '../../../services/voucherAmount';
import styles from './styles';


class VoucherConfirmedPage extends React.Component {
  state = { };

  componentWillMount() {
    const { bookshopCustomer, resetForm } = this.props;

    if (Object.keys(bookshopCustomer).length === 0) {
      history.push('/basket');
    }

    resetForm();
  }

  /**
   * Checks the amount before asking "are you sure".
   *
   * The dialog used to open whatever was typed - including nothing at all -
   * and the amount was only checked after the shop had confirmed, so the
   * operator answered a question about a redemption that was never going to
   * happen. Same rules as the standalone Redeem voucher screen.
   */
  onSubmit = () => {
    const { value, bookshopCustomer } = this.props;
    const balance = (bookshopCustomer.Voucher || {}).Balance;
    const error = redeemAmountError(value, balance);

    if (error) {
      this.props.validateRedeemValue({ status: false, message: error });
      return;
    }

    this.props.openConfirmModal();
  }

  render() {
    const {
      classes,
      loading,
      bookshopCustomer,
      value,
      valueStatus,
      isOpenConfirmModal,
      changeRedeemValue,
      closeConfirmModal,
      redeemVoucher,
    } = this.props;

    // No PaperLayout here: this screen is mounted inside the account shell so
    // the shop keeps its navigation tabs, and that shell already supplies the
    // page chrome. Wrapping it again would nest <Navigation> twice.
    return (
      <div>
        <h4 className={classes.screenTitle}>VOUCHER CONFIRMED</h4>
        <div className={classes.container}>
          <ConfirmModal
            modal={isOpenConfirmModal}
            onAgree={redeemVoucher}
            closeModal={closeConfirmModal}
            question={'Are you sure you want to redeem a voucher?'}
          />
          { loading
            ? (
              <div className={classes.loaderContainer}>
                <Loader />
              </div>
            )
            : (
              <div>
                <VoucherInfo bookshopCustomer={bookshopCustomer} />
                {/* This voucher's own balance, not the customer's retired
                    pooled balance - matches what RedeemVoucher enforces. */}
                <Redeem
                  value={value}
                  balance={(bookshopCustomer.Voucher || {}).Balance}
                  valueStatus={valueStatus}
                  changeRedeemValue={changeRedeemValue}
                  openConfirmModal={this.onSubmit}
                />
                {/* The 'Continue' button used to be the only way off this
                    screen, and it dead-ended on one customer's history. The
                    account tabs are now present throughout, so the shop can
                    go wherever it needs instead. */}
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

VoucherConfirmedPage.propTypes = {
  classes: PropTypes.object,
  loading: PropTypes.bool,
  bookshopCustomer: PropTypes.object,
  value: PropTypes.string,
  valueStatus: PropTypes.object,
  isOpenConfirmModal: PropTypes.bool,

  changeRedeemValue: PropTypes.func,
  openConfirmModal: PropTypes.func,
  validateRedeemValue: PropTypes.func,
  closeConfirmModal: PropTypes.func,
  redeemVoucher: PropTypes.func,
  resetForm: PropTypes.func,
};

VoucherConfirmedPage.defaultProps = {
  classes: {},
  loading: false,
  bookshopCustomer: {},
  value: '',
  valueStatus: {},
  isOpenConfirmModal: false,

  changeRedeemValue: () => { },
  openConfirmModal: () => { },
  closeConfirmModal: () => { },
  redeemVoucher: () => { },
  resetForm: () => { },
};

const mapStateToProps = (state) => {
  return {
    loading: getLoading(state),
    bookshopCustomer: getBookshopCustomer(state),
    value: getValue(state),
    valueStatus: getValueStatus(state),
    isOpenConfirmModal: getConfirmModal(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    changeRedeemValue: (value, field) => dispatch(voucherConfirmedActions.changeRedeemValue(value, field)),
    openConfirmModal: () => dispatch(voucherConfirmedActions.openConfirmModal()),
    validateRedeemValue: payload => dispatch(voucherConfirmedActions.validateRedeemValue(payload)),
    closeConfirmModal: () => dispatch(voucherConfirmedActions.closeConfirmModal()),
    redeemVoucher: () => dispatch(voucherConfirmedActions.redeemVoucher()),
    resetForm: () => dispatch(voucherConfirmedActions.resetForm()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(VoucherConfirmedPage));
